import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Archive, ChevronDown, ChevronRight, FileText, CheckCircle2,
  Clock, RefreshCw, Loader2, BrainCircuit, Wand2,
} from "lucide-react";
import { getArchive, getArchivedRecords } from "./api";
import ReclassifyModal from "./ReclassifyModal";

const VAULT_LABEL = {
  TAX_CUSTODY: "Tax", SUPPLIER_OBLIGATION: "Supplier",
  CUSTOMER_TRUST: "Customer", INVENTORY_CUSTODY: "Inventory",
  OPERATING_CUSTODY: "Operating", FREE_EQUITY: "Free Equity",
  CREDIT_FACILITY_CUSTODY: "Credit", LOAN_OBLIGATION: "Loan",
  RESERVE_CAPITAL: "Reserve", STRATEGIC_CAPITAL: "Strategic",
};

function formatGBP(n) {
  const v = Number(n) || 0;
  return `${v < 0 ? "-" : ""}£${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/* Per-batch Sentinel score — derived deterministically from vault_summary.
   Returns the headline numbers for FREE_EQUITY, TAX_CUSTODY, OPERATING_CUSTODY
   and SUPPLIER_OBLIGATION plus the net financial impact of the batch. */
function computeBatchSentinelScore(vaultSummary) {
  const vs = vaultSummary || {};
  const freeEquity = vs.FREE_EQUITY || 0;
  const tax = vs.TAX_CUSTODY || 0;
  const operating = vs.OPERATING_CUSTODY || 0;
  const supplier = vs.SUPPLIER_OBLIGATION || 0;
  const net = Object.values(vs).reduce((a, b) => a + (Number(b) || 0), 0);
  return { freeEquity, tax, operating, supplier, net };
}

/* Document Archive — every batch ever uploaded by this org.
   Always visible. Records are fetched on-demand when a row is expanded.
   This is the permanent fix for "documents disappearing": even if state
   is wiped on the active page, the archive serves them straight from the
   ledger via /api/datapool/archive. */
export default function DocumentArchive({ onSelectBatch }) {
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [recordsByBatch, setRecordsByBatch] = useState({});
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [reclassifyBatchId, setReclassifyBatchId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getArchive();
      setArchive(res.data.archive || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleExpand = async (batchId) => {
    if (expanded === batchId) { setExpanded(null); return; }
    setExpanded(batchId);
    if (recordsByBatch[batchId]) return;
    setRecordsLoading(true);
    try {
      const res = await getArchivedRecords(batchId);
      setRecordsByBatch((prev) => ({ ...prev, [batchId]: res.data.records || [] }));
    } catch (e) {
      // Never wipe on error — just log
      console.error("archive records load failed", e);
    } finally {
      setRecordsLoading(false);
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-800/50" data-testid="document-archive">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-barlow font-bold text-slate-200">
              Document Archive
              <span className="text-[11px] text-slate-500 font-normal ml-2" data-testid="archive-count">
                {archive.length} batch{archive.length === 1 ? "" : "es"}
              </span>
            </h3>
          </div>
          <Button onClick={refresh} disabled={loading} size="sm" variant="ghost"
            className="h-7 text-[11px] text-slate-500 hover:text-slate-300 gap-1" data-testid="archive-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Permanent record of every batch this organisation has ever uploaded. Records here never disappear.
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-md bg-red-500/[0.06] border border-red-500/20 text-[11px] text-red-300" data-testid="archive-error">
            {String(error)}
          </div>
        )}

        {!loading && archive.length === 0 && (
          <div className="px-4 py-6 rounded-lg border border-slate-800/40 bg-slate-900/30 text-center" data-testid="archive-empty">
            <p className="text-xs text-slate-400">No documents uploaded yet.</p>
            <p className="text-[10px] text-slate-600 mt-0.5">Upload financial files above to begin.</p>
          </div>
        )}

        {archive.length > 0 && (
          <div className="space-y-1.5" data-testid="archive-list">
            {archive.map((b) => {
              const open = expanded === b.batch_id;
              const recs = recordsByBatch[b.batch_id] || [];
              const summaryEntries = Object.entries(b.vault_summary || {}).filter(([, v]) => Math.abs(v) > 0);
              return (
                <div key={b.batch_id}
                  className="rounded-lg border border-slate-800/40 bg-slate-900/30 overflow-hidden"
                  data-testid={`archive-row-${b.batch_id}`}>
                  <button onClick={() => handleExpand(b.batch_id)}
                    className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800/30 transition-colors text-left"
                    data-testid={`archive-toggle-${b.batch_id}`}>
                    {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                    <span className="text-[10px] text-slate-500 font-mono shrink-0 w-20">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                    </span>
                    <span className="text-[10px] text-slate-600 font-mono shrink-0 w-16">{b.batch_id?.slice(0, 8)}</span>
                    <FileText className="w-3 h-3 text-slate-600 shrink-0" />
                    <span className="text-xs text-slate-400 shrink-0 w-16">{b.file_count} file{b.file_count === 1 ? "" : "s"}</span>
                    <span className="text-xs text-cyan-400 font-mono shrink-0 w-20" data-testid={`archive-records-${b.batch_id}`}>
                      {b.record_count} rec
                    </span>
                    <Badge className={`text-[9px] border-none shrink-0 ${b.committed ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                      {b.committed ? <><CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> Committed</> : <><Clock className="w-2.5 h-2.5 inline mr-0.5" /> Pending</>}
                    </Badge>
                    {summaryEntries.length > 0 && (
                      <div className="hidden lg:flex items-center gap-1 ml-auto flex-wrap">
                        {summaryEntries.slice(0, 4).map(([v, amt]) => (
                          <span key={v} className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${amt < 0 ? "text-red-400 bg-red-500/[0.06]" : "text-emerald-400 bg-emerald-500/[0.06]"}`}>
                            {VAULT_LABEL[v] || v}: {amt < 0 ? "-" : "+"}{formatGBP(Math.abs(amt)).replace("£", "£")}
                          </span>
                        ))}
                      </div>
                    )}
                    {onSelectBatch && (
                      <span onClick={(e) => { e.stopPropagation(); onSelectBatch(b.batch_id); }}
                        className="ml-auto text-[10px] text-cyan-400 hover:text-cyan-300 cursor-pointer shrink-0"
                        data-testid={`archive-select-${b.batch_id}`}>
                        Open →
                      </span>
                    )}
                  </button>

                  {/* Phase 4 — Per-Batch Sentinel Score line.
                      Shows the financial impact of this batch in one line:
                      net position + tax + supplier + operating contributions.
                      Net is colour-coded green/red. */}
                  {b.committed && summaryEntries.length > 0 && (() => {
                    const score = computeBatchSentinelScore(b.vault_summary);
                    return (
                      <div
                        className="px-3 pb-2 pt-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-slate-400 border-t border-slate-800/30"
                        data-testid={`archive-sentinel-score-${b.batch_id}`}
                      >
                        <span className="flex items-center gap-1 text-cyan-400 mt-1.5">
                          <BrainCircuit className="w-3 h-3" />
                          <span className="text-[9px] uppercase tracking-[0.1em] font-semibold">Sentinel</span>
                        </span>
                        {score.freeEquity !== 0 && (
                          <span className="mt-1.5">
                            <span className={score.freeEquity >= 0 ? "text-emerald-400" : "text-red-400"}>
                              {score.freeEquity >= 0 ? "+" : "-"}
                              {formatGBP(Math.abs(score.freeEquity))}
                            </span>{" "}
                            <span className="text-slate-500">to Free Equity</span>
                          </span>
                        )}
                        {score.tax !== 0 && (
                          <span className="text-slate-500 mt-1.5">·{" "}
                            <span className="text-amber-400">{formatGBP(Math.abs(score.tax))}</span> tax
                            {score.tax < 0 ? " obligation" : " inflow"}
                          </span>
                        )}
                        {score.supplier !== 0 && (
                          <span className="text-slate-500 mt-1.5">·{" "}
                            <span className="text-orange-400">{formatGBP(Math.abs(score.supplier))}</span> supplier
                            {score.supplier < 0 ? " obligation" : " inflow"}
                          </span>
                        )}
                        {score.operating !== 0 && (
                          <span className="text-slate-500 mt-1.5">·{" "}
                            <span className="text-rose-400">{formatGBP(Math.abs(score.operating))}</span> operating cost
                          </span>
                        )}
                        <span className="text-slate-500 mt-1.5">· Net:{" "}
                          <span
                            className={`font-semibold ${score.net >= 0 ? "text-emerald-400" : "text-red-400"}`}
                            data-testid={`archive-sentinel-net-${b.batch_id}`}
                          >
                            {score.net >= 0 ? "+" : "-"}{formatGBP(Math.abs(score.net))}
                          </span>
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setReclassifyBatchId(b.batch_id); }}
                          className="ml-auto mt-1.5 inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
                          data-testid={`archive-reclassify-btn-${b.batch_id}`}
                        >
                          <Wand2 className="w-3 h-3" /> Re-classify
                        </button>
                      </div>
                    );
                  })()}
                  {/* For pending (non-committed) batches, still surface a
                      Re-classify entry-point even without a Sentinel score. */}
                  {!b.committed && (
                    <div className="px-3 pb-2 pt-0 flex justify-end border-t border-slate-800/30">
                      <button
                        onClick={(e) => { e.stopPropagation(); setReclassifyBatchId(b.batch_id); }}
                        className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
                        data-testid={`archive-reclassify-btn-${b.batch_id}`}
                      >
                        <Wand2 className="w-3 h-3" /> Re-classify
                      </button>
                    </div>
                  )}

                  {open && (
                    <div className="border-t border-slate-800/40 bg-slate-900/40 px-3 py-2" data-testid={`archive-records-${b.batch_id}-panel`}>
                      {recordsLoading && recs.length === 0 && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 py-2">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading records...
                        </div>
                      )}
                      {!recordsLoading && recs.length === 0 && (
                        <div className="text-[11px] text-slate-500 py-2">No records in this batch.</div>
                      )}
                      {recs.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-slate-600 uppercase text-[9px] tracking-wider">
                                <th className="text-left font-normal py-1 pr-3">Date</th>
                                <th className="text-left font-normal py-1 pr-3">Description</th>
                                <th className="text-right font-normal py-1 pr-3">Amount</th>
                                <th className="text-left font-normal py-1 pr-3">Direction</th>
                                <th className="text-left font-normal py-1 pr-3">Vault</th>
                                <th className="text-left font-normal py-1">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recs.map((r) => {
                                const np = r.normalized_payload || {};
                                const ai = r.ai_result || {};
                                const allocs = r.final_allocation || ai.recommended_vault_allocations || [];
                                const vault = allocs[0]?.vault || "—";
                                return (
                                  <tr key={r.id} className="border-t border-slate-800/30" data-testid={`archive-record-${r.id}`}>
                                    <td className="py-1.5 pr-3 text-slate-500 font-mono">{(np.date || "").slice(0, 10)}</td>
                                    <td className="py-1.5 pr-3 text-slate-300 truncate max-w-xs">{np.description || "—"}</td>
                                    <td className={`py-1.5 pr-3 text-right font-mono ${np.direction === "inflow" ? "text-emerald-400" : "text-red-400"}`}>
                                      {np.direction === "inflow" ? "+" : "-"}
                                      {(np.source_currency || "GBP")} {(np.source_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="py-1.5 pr-3 text-slate-400 capitalize">{np.direction || "—"}</td>
                                    <td className="py-1.5 pr-3 text-cyan-400">{VAULT_LABEL[vault] || vault}</td>
                                    <td className="py-1.5">
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                        r.status === "committed" ? "bg-purple-500/15 text-purple-400" :
                                        r.status === "approved" ? "bg-cyan-500/15 text-cyan-400" :
                                        r.status === "safe" ? "bg-emerald-500/15 text-emerald-400" :
                                        r.status === "needs_review" ? "bg-amber-500/15 text-amber-400" :
                                        "bg-slate-500/15 text-slate-400"
                                      }`}>{r.status}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <ReclassifyModal
        batchId={reclassifyBatchId}
        open={!!reclassifyBatchId}
        onClose={() => setReclassifyBatchId(null)}
        onComplete={() => refresh()}
      />
    </Card>
  );
}
